import React from 'react';
import { MapPin, Store, ArrowRight, Clock } from 'lucide-react';

/**
 * Dealer network page — honest pre-launch state.
 *
 * The dealer network is being built alongside the Scarborough warehouse & retail
 * opening (move-in October 1, 2026). Until stockists are live, this page routes
 * people to buy direct or apply for a wholesale account. Do NOT list retailers
 * here that have not confirmed they stock our product.
 */
const DealerLocator: React.FC = () => {
  return (
    <div className="bg-paper min-h-screen">
      {/* Header */}
      <header className="bg-forge text-paper py-20 lg:py-24 px-6 lg:px-10 relative">
        <div className="max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-5">
            — Where to buy
          </p>
          <h1 className="font-editorial italic font-light text-4xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-5">
            The dealer network is coming.
          </h1>
          <p className="text-paper/70 text-lg max-w-prose leading-[1.6]">
            We are building our retail stockist network alongside the opening of our Scarborough
            warehouse &amp; retail location this fall. Until then, every blade ships direct from us.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      <div className="max-w-editorial mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {/* Buy direct */}
          <div className="border border-ink-200 bg-paper-bright p-8 lg:p-10 flex flex-col">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
              — Available today
            </p>
            <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-tight mb-3">
              Buy direct.
            </h2>
            <p className="text-sm text-ink-700 leading-relaxed mb-8 flex-1">
              The full Incolma collection, shipped from our Toronto-area hub. No minimum order for
              retail customers.
            </p>
            <a
              href="/shop"
              className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-6 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial"
            >
              Shop the collection
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>

          {/* Become a stockist */}
          <div className="border border-ember bg-ember/5 p-8 lg:p-10 flex flex-col">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">
              — For retailers
            </p>
            <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-tight mb-3">
              Become a stockist.
            </h2>
            <p className="text-sm text-ink-700 leading-relaxed mb-8 flex-1">
              Carry the only authentic Incolma line in Canada. Founding dealers get
              priority allocation and placement on this page when the network goes live.
            </p>
            <a
              href="/apply"
              className="inline-flex items-center justify-center gap-2 bg-forge hover:bg-forge-deep text-paper-warm px-6 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial"
            >
              <Store className="w-4 h-4" strokeWidth={1.5} />
              Apply for a wholesale account
            </a>
          </div>
        </div>

        {/* Scarborough counter */}
        <div className="p-8 lg:p-10 bg-forge text-paper">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="w-12 h-12 bg-ember flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-paper-warm" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-editorial italic text-2xl text-paper-warm mb-2 tracking-tight">
                Visit us in Scarborough.
              </h3>
              <p className="text-sm text-paper/75 leading-relaxed max-w-prose">
                We&apos;re in at{' '}
                <span className="text-paper-warm font-medium">345 Nugget Avenue, Unit 17, Scarborough</span>.
                Order pickup and wholesale will-call are open now, by appointment — confirm a time on
                WhatsApp. The public retail counter&apos;s grand opening date will be published here.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-micro uppercase text-brass border border-paper/15 px-4 py-3 flex-shrink-0">
              <Clock className="w-4 h-4" strokeWidth={1.5} />
              Pickup open · by appointment
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerLocator;
